import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_MODE,
  DEFAULT_PALETTE,
  THEME_IDS,
  THEMES,
  applyThemeToDocument,
  isPalette,
  parseThemeInput
} from './themes'

const ThemeContext = createContext(null)

/** Theme comes only from ?theme=palette or ?theme=palette-light (default = dark). */
const resolveTheme = (raw, fallbackRaw) => {
  const parsed = parseThemeInput(raw) || parseThemeInput(fallbackRaw)
  return {
    palette: parsed?.palette && isPalette(parsed.palette) ? parsed.palette : DEFAULT_PALETTE,
    mode: parsed?.mode === 'light' ? 'light' : DEFAULT_MODE
  }
}

const readQueryThemeParam = () => {
  if (typeof window === 'undefined') return null
  try {
    return new URLSearchParams(window.location.search).get('theme')
  } catch {
    return null
  }
}

export const ThemeProvider = ({ children, defaultTheme }) => {
  const [themeState, setThemeState] = useState(() => {
    const initial = resolveTheme(readQueryThemeParam(), defaultTheme)
    applyThemeToDocument(initial.palette, initial.mode)
    return initial
  })

  useEffect(() => {
    applyThemeToDocument(themeState.palette, themeState.mode)
  }, [themeState])

  // Host casino can sync theme into the iframe (same shapes as ?theme=)
  useEffect(() => {
    const onMessage = (event) => {
      const payload = event?.data
      if (!payload || typeof payload !== 'object') return

      const name = payload.eventName || payload.type
      if (name !== 'SET_THEME' && name !== 'setTheme' && name !== 'THEME') return

      const parsed = parseThemeInput(payload.data ?? payload.theme ?? payload.palette)
      if (!parsed?.palette || !isPalette(parsed.palette)) return

      setThemeState({
        palette: parsed.palette,
        mode: parsed.mode === 'light' ? 'light' : DEFAULT_MODE
      })
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const value = useMemo(() => ({
    theme: themeState.mode === 'light'
      ? `${themeState.palette}-light`
      : themeState.palette,
    palette: themeState.palette,
    mode: themeState.mode,
    isDark: themeState.mode === 'dark',
    themes: THEMES,
    themeIds: THEME_IDS
  }), [themeState])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

export default ThemeContext
